import React, { useState } from 'react';
import { HiOutlineCheck, HiOutlineClipboardDocument, HiOutlineInformationCircle } from 'react-icons/hi2';
import { copyText } from '../utils/clipboard';

// ChatGPT connects by sign-in — it has no API-key or custom-header option at all,
// so the token config the other MCP clients use is unusable there.
//
// ⚠️ Say "Plugins", never "Connectors": OpenAI renamed the surface (directory
// migrated 2026-07-09) and the old wording names menus that no longer exist.

const UI = ({ children }) => (
  <span className="font-semibold text-gray-900 dark:text-white whitespace-nowrap">{children}</span>
);

// Exported so every surface that walks someone through ChatGPT reads from one
// list — this component (Integrations card) and the /connect?mcp=chatgpt wizard,
// which renders them in its own numbered-step chrome.
export const CHATGPT_SETUP_STEPS = [
  <>In ChatGPT, open <UI>Settings → Security and login</UI> and turn on <UI>Developer mode</UI>.</>,
  <>Go to <UI>Settings → Plugins</UI> and click the <UI>+</UI> button.</>,
  <>Give it a name, paste the URL above, and pick <UI>OAuth</UI> as the authentication method.</>,
  <>Create it, then connect — sign in and choose your workspace and access level.</>,
];

export const CHATGPT_REQUIREMENTS = (
  <>
    Needs ChatGPT Plus, Pro, Business, Enterprise or Edu, on the web — MCP apps aren&rsquo;t
    available on mobile. On Business, Enterprise and Edu an admin has to enable developer mode
    for the workspace first.
  </>
);

const ChatGptSetupSteps = ({ mcpUrl }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyText(mcpUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
        ChatGPT signs in instead of using a token. Copy this URL, then follow the four steps:
      </p>

      <div className="rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-gray-900/60 overflow-hidden">
        <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 px-3.5 pt-3 pb-3 whitespace-pre-wrap break-all select-all m-0 leading-relaxed">{mcpUrl}</pre>
        <div className="px-2 pb-2">
          <button
            onClick={handleCopy}
            className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white rounded-lg transition-all duration-200 ${
              copied ? 'bg-emerald-500' : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 shadow-sm shadow-cyan-500/25'
            }`}
          >
            {copied ? (
              <><HiOutlineCheck className="w-4 h-4" strokeWidth={2.5} /> Copied — now paste it</>
            ) : (
              <><HiOutlineClipboardDocument className="w-4 h-4" /> Copy URL</>
            )}
          </button>
        </div>
      </div>

      <ol className="space-y-2.5">
        {CHATGPT_SETUP_STEPS.map((step, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className="flex-shrink-0 w-5 h-5 mt-0.5 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm shadow-cyan-500/30">
              {i + 1}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{step}</span>
          </li>
        ))}
      </ol>

      <div className="flex gap-2.5 px-3.5 py-3 rounded-xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-200/60 dark:border-white/[0.06]">
        <HiOutlineInformationCircle className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          {CHATGPT_REQUIREMENTS}
        </p>
      </div>
    </div>
  );
};

export default ChatGptSetupSteps;
